import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MessageSquare, Users, Send, AlertCircle, CheckCircle, Search } from 'lucide-react';
import api from '../../lib/axios';

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-all -mb-px whitespace-nowrap
        ${active
          ? 'border-primary text-primary'
          : 'border-transparent text-ink-muted hover:text-ink hover:border-surface-border'}`}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  const { sent = 0, failed = 0, skipped = 0 } = result;
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
      <CheckCircle size={16} className="text-emerald-600 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-emerald-800">Messages sent</p>
        <p className="text-xs text-emerald-700 mt-0.5">
          Sent: <strong>{sent}</strong>
          {failed > 0 && <> · Failed: <strong className="text-red-600">{failed}</strong></>}
          {skipped > 0 && <> · Skipped (no phone): <strong>{skipped}</strong></>}
        </p>
      </div>
      <button onClick={onDismiss} className="text-emerald-500 hover:text-emerald-700 text-lg leading-none">×</button>
    </div>
  );
}

function ErrorBanner({ error }) {
  if (!error) return null;
  const msg = error?.response?.data?.error?.message || error?.message || 'Failed to send messages.';
  const isTwilio = msg.toLowerCase().includes('twilio') || msg.toLowerCase().includes('sms');
  return (
    <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200">
      <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-red-700">{isTwilio ? 'Twilio not configured' : 'Send failed'}</p>
        <p className="text-xs text-red-600 mt-0.5">{msg}</p>
        {isTwilio && (
          <p className="text-xs text-red-500 mt-1">
            Go to <strong>Settings → Messaging (Twilio)</strong> to configure your Twilio credentials.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Tab 1: Send to single client ──────────────────────────────────────────────
function SendMessage({ clients }) {
  const [selectedClientId, setSelectedClientId] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);

  const mutation = useMutation({
    mutationFn: ({ clientIds, message }) => api.post('/messages/send', { clientIds, message }).then(r => r.data.data),
    onSuccess: (data) => { setResult(data); setMessage(''); setSelectedClientId(''); },
  });

  const send = () => {
    if (!selectedClientId || !message.trim()) return;
    mutation.mutate({ clientIds: [selectedClientId], message: message.trim() });
  };

  return (
    <div className="space-y-4">
      {result && <ResultBanner result={result} onDismiss={() => setResult(null)} />}
      {mutation.isError && !result && <ErrorBanner error={mutation.error} />}

      <div className="card p-5 space-y-4">
        <div>
          <label className="label">Select client</label>
          <select
            value={selectedClientId}
            onChange={e => setSelectedClientId(e.target.value)}
            className="input w-full"
          >
            <option value="">— Choose a client —</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.phone ? ` (${c.phone})` : ' (no phone)'}
              </option>
            ))}
          </select>
          {selectedClientId && !clients.find(c => c.id === selectedClientId)?.phone && (
            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
              <AlertCircle size={11} /> This client has no phone number — SMS will be skipped.
            </p>
          )}
        </div>

        <div>
          <label className="label">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            className="input resize-none w-full"
            placeholder="Type your message here…"
          />
          <p className="text-xs text-ink-muted mt-1">{message.length} characters</p>
        </div>

        <button
          onClick={send}
          disabled={!selectedClientId || !message.trim() || mutation.isPending}
          className="btn btn-primary flex items-center gap-2"
        >
          <Send size={14} />
          {mutation.isPending ? 'Sending…' : 'Send Message'}
        </button>
      </div>
    </div>
  );
}

// ── Tab 2: Bulk Message ───────────────────────────────────────────────────────
function BulkMessage({ clients }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);

  const filtered = clients.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.phone || '').includes(search)
  );

  const allSelected = filtered.length > 0 && filtered.every(c => selected.has(c.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filtered.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: ({ clientIds, message }) => api.post('/messages/send', { clientIds, message }).then(r => r.data.data),
    onSuccess: (data) => { setResult(data); setMessage(''); setSelected(new Set()); },
  });

  const send = () => {
    if (selected.size === 0 || !message.trim()) return;
    mutation.mutate({ clientIds: Array.from(selected), message: message.trim() });
  };

  return (
    <div className="space-y-4">
      {result && <ResultBanner result={result} onDismiss={() => setResult(null)} />}
      {mutation.isError && !result && <ErrorBanner error={mutation.error} />}

      <div className="card overflow-hidden">
        {/* Search + select all */}
        <div className="p-3 border-b border-surface-border flex items-center gap-3">
          <input
            type="checkbox"
            checked={allSelected}
            onChange={toggleAll}
            className="w-4 h-4 accent-primary"
          />
          <div className="flex items-center gap-2 flex-1">
            <Search size={13} className="text-ink-muted shrink-0" />
            <input
              className="flex-1 text-sm bg-transparent outline-none text-ink placeholder-ink-muted"
              placeholder="Filter clients…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <span className="text-xs text-ink-muted shrink-0">{selected.size} selected</span>
        </div>

        <div className="max-h-64 overflow-y-auto divide-y divide-surface-border">
          {filtered.length === 0 && (
            <div className="p-6 text-center text-ink-muted text-sm">No clients found.</div>
          )}
          {filtered.map(c => (
            <label key={c.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                onChange={() => toggle(c.id)}
                className="w-4 h-4 accent-primary"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-ink">{c.name}</p>
                <p className="text-xs text-ink-muted truncate">
                  {c.phone || <span className="text-amber-500">No phone</span>}
                  {c.email && ` · ${c.email}`}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <div>
          <label className="label">Message</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            className="input resize-none w-full"
            placeholder="Type your message here…"
          />
          <p className="text-xs text-ink-muted mt-1">{message.length} characters</p>
        </div>

        <button
          onClick={send}
          disabled={selected.size === 0 || !message.trim() || mutation.isPending}
          className="btn btn-primary flex items-center gap-2"
        >
          <Send size={14} />
          {mutation.isPending ? 'Sending…' : `Send to ${selected.size} Selected`}
        </button>
      </div>
    </div>
  );
}

// ── Main Messages page ────────────────────────────────────────────────────────
export default function Messages() {
  const [tab, setTab] = useState('single');

  const { data, isLoading } = useQuery({
    queryKey: ['clients-all'],
    queryFn: () => api.get('/clients?limit=1000').then(r => r.data.data),
    staleTime: 60_000,
  });

  const clients = data || [];

  return (
    <div className="p-6 max-w-2xl">
      <div className="mb-5">
        <h1 className="text-lg font-semibold text-ink flex items-center gap-2">
          <MessageSquare size={20} className="text-primary" />
          Messages
        </h1>
        <p className="text-sm text-ink-secondary mt-0.5">Send SMS messages to individual clients or in bulk</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-border mb-5">
        <TabButton active={tab === 'single'} onClick={() => setTab('single')} icon={MessageSquare} label="Send Message" />
        <TabButton active={tab === 'bulk'}   onClick={() => setTab('bulk')}   icon={Users}         label="Bulk Message" />
      </div>

      {isLoading ? (
        <div className="card p-10 text-center text-ink-muted text-sm">Loading clients…</div>
      ) : tab === 'single' ? (
        <SendMessage clients={clients} />
      ) : (
        <BulkMessage clients={clients} />
      )}
    </div>
  );
}
